import React, { useEffect, useRef } from 'react'
import {
  DashboardAreaKey,
  DashboardAreaProperties,
  DashboardAreasConfiguration,
  WidgetAreasConfiguration,
  DashboardAreaKeys,
  AreaEditorProps,
} from '../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, MenuItem } from '@mui/material'
import Droppable from './Droppable'
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element'
import { CurrentValuesArea, SystemLogAndMapArea, SavingsArea, StatisticsArea, SummaryArea, WidgetsArea } from './areas'
import { DASHBOARD_EDITOR_CONTENT_PAGE_ID } from 'src/Libs/qa/ids'

const WIDGETS_AREA_NAME = 'widgets'
const NOT_FOUND_INDEX = -1

interface EditorPageProps {
  widgetAreasConfiguration: WidgetAreasConfiguration
  setWidgetAreasConfiguration: (areasConfiguration: WidgetAreasConfiguration) => void
  dashboardAreasConfiguration: DashboardAreasConfiguration
  setDashboardAreasConfiguration: (areas: DashboardAreasConfiguration) => void
  isDynamicPowerPriceEnabled?: boolean
}

const dashboardAreasComponentMap: { [key in DashboardAreaKey]: React.FC<AreaEditorProps> } = {
  currentValues: CurrentValuesArea,
  summary: SummaryArea,
  widgets: WidgetsArea,
  systemLogAndMap: SystemLogAndMapArea,
  statistics: StatisticsArea,
  savings: SavingsArea,
}

/**
 * EditorContent component for the dashboard editor
 */
const EditorContent: React.FC<EditorPageProps> = ({
  setDashboardAreasConfiguration,
  dashboardAreasConfiguration,
  widgetAreasConfiguration,
  setWidgetAreasConfiguration,
  isDynamicPowerPriceEnabled,
}) => {
  const { t } = useCustomTranslation()

  /* sort areas by position and add name, to be able to find specific areas */
  const areas = Object.entries<DashboardAreaProperties>(dashboardAreasConfiguration)
    .sort(([_keyA, valueA], [_keyB, valueB]) => valueA.position - valueB.position)
    .map(([key, value]) => ({ ...value, name: key as DashboardAreaKey }))

  const availableAreasAmount = areas.length - (isDynamicPowerPriceEnabled ? 0 : 1)
  const hiddenAreasAmount = areas.filter(area => area.hidden).length

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    return autoScrollForElements({
      element,
    })
  }, [])

  const handleOnDrop = (oldPosition: number, newPosition: number, name: string): void => {
    if (oldPosition === newPosition) return

    const areasArray = Object.entries(dashboardAreasConfiguration).map(([key, value]) => ({ key, ...value }))
    const indexToMove = areasArray.findIndex(area => area.key === name)

    if (indexToMove === NOT_FOUND_INDEX) return

    const [movedItem] = areasArray.splice(indexToMove, 1)
    areasArray.splice(oldPosition < newPosition ? newPosition - 1 : newPosition, 0, movedItem)

    const updatedAreas = areasArray.reduce<DashboardAreasConfiguration>((acc, area, index) => {
      return { ...acc, [area.key]: { ...area, position: index } }
    }, {} as DashboardAreasConfiguration)

    setDashboardAreasConfiguration(updatedAreas)
  }

  const handleMoveToTop = (position: number, name: string): void => {
    handleOnDrop(position, 0, name)
  }

  const handleMoveUp = (position: number, name: string): void => {
    handleOnDrop(position, position - 1, name)
  }

  const handleMoveDown = (position: number, name: string): void => {
    /* position + 2, because we have 1 more position than areas */
    handleOnDrop(position, position + 2, name)
  }

  const handleMoveToBottom = (position: number, name: string): void => {
    handleOnDrop(position, areas.length, name)
  }

  const getMenuItems = (onClose: () => void, position: number, name: string): React.ReactNode[] => [
    <MenuItem
      key={'move-to-top'}
      onClick={() => {
        handleMoveToTop(position, name)
        onClose()
      }}
      disabled={position === 0}
    >
      {t('dashboard.editor.move-to-top')}
    </MenuItem>,
    <MenuItem
      key={'move-up'}
      onClick={() => {
        handleMoveUp(position, name)
        onClose()
      }}
      disabled={position === 0}
    >
      {t('dashboard.editor.move-up')}
    </MenuItem>,
    <MenuItem
      key={'move-down'}
      onClick={() => {
        handleMoveDown(position, name)
        onClose()
      }}
      disabled={position === areas.length - 1}
    >
      {t('dashboard.editor.move-down')}
    </MenuItem>,
    <MenuItem
      key={'move-to-bottom'}
      onClick={() => {
        handleMoveToBottom(position, name)
        onClose()
      }}
      disabled={position === areas.length - 1}
    >
      {t('dashboard.editor.move-to-bottom')}
    </MenuItem>,
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, userSelect: 'none' }} ref={ref} data-qa={DASHBOARD_EDITOR_CONTENT_PAGE_ID}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 0 }}>
        {areas.map((area, index) => {
          const AreaComponent = dashboardAreasComponentMap[area.name]
          const isWidgetComponent = area.name === WIDGETS_AREA_NAME

          const disabled = area.name === DashboardAreaKeys.savings && !isDynamicPowerPriceEnabled
          const isLastDisplayedWidget = hiddenAreasAmount === availableAreasAmount - 1

          const setHidden = (value: boolean): void => {
            if (value === true && isLastDisplayedWidget) return
            setDashboardAreasConfiguration({ ...dashboardAreasConfiguration, [area.name]: { position: area.position, hidden: value } })
          }

          return (
            <>
              {index === 0 && <Droppable index={index} handleOnDrop={handleOnDrop} />}
              <AreaComponent
                key={area.name}
                hidden={area.hidden}
                setHidden={setHidden}
                {...(isWidgetComponent && { widgetAreasConfiguration, setWidgetAreasConfiguration })}
                position={area.position}
                getMenuItems={onClose => getMenuItems(onClose, area.position, area.name)}
                isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
                disabled={disabled}
              />
              <Droppable index={index + 1} handleOnDrop={handleOnDrop} />
            </>
          )
        })}
      </Box>
    </Box>
  )
}

export default EditorContent
